import { useState, useEffect, useCallback, memo } from 'react';
import * as Location from 'expo-location';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { View, useColorScheme } from 'react-native';
import darkMapStyle from './map-styles/dark.json';
import lightMapStyle from './map-styles/light.json';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { setSelectedBusiness } from '../redux/businessListSlice';
import { Business } from '../types';
import { ActivityIndicator, Text } from 'react-native-paper';
import { useAppTheme } from '../theme';
import { setUsersLocation } from '../redux/userSlice';
import Fuse from 'fuse.js';

interface Props {
  webMap: google.maps.Map | null;
  setWebMap: React.Dispatch<React.SetStateAction<google.maps.Map | null>>;
}

const libraries: (
  | 'geometry'
  | 'places'
  | 'drawing'
  | 'localContext'
  | 'visualization'
)[] = ['places'];

function WebMap({ webMap, setWebMap }: Props) {
  const theme = useAppTheme();
  const colorScheme = useColorScheme();
  const dispatch = useAppDispatch();

  const { businesses, selectedBusiness, filterOptions, businessSearchText } =
    useAppSelector((state) => state.businessList);
  const { usersLocation } = useAppSelector((state) => state.user);

  const [isLoadingUserLocation, setLoadingUserLocation] = useState(true);

  useEffect(() => {
    (async () => {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        console.log('Permission to access location was denied');
        setLoadingUserLocation(false);
        return;
      }

      const location = await Location.getCurrentPositionAsync({});
      const { latitude, longitude } = location.coords;

      dispatch(setUsersLocation({ latitude, longitude }));
      setLoadingUserLocation(false);
    })();
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyDcXSuxcb04ME1OxjH6LS9lgNAoKx0erkk',
    libraries,
  });

  const onLoad = useCallback(
    function callback(map: google.maps.Map) {
      map.setCenter({
        lat: usersLocation.latitude,
        lng: usersLocation.longitude,
      });
      map.setZoom(15);
      setWebMap(map);
    },
    [usersLocation]
  );

  const onUnmount = useCallback(function callback() {
    setWebMap(null);
  }, []);

  function onMarkerClick(business: Business) {
    dispatch(setSelectedBusiness(business));
    webMap?.panTo({ lat: business.latitude, lng: business.longitude });
  }

  function getMarkerOpacity(business: Business) {
    let opacity = 1;
    if (selectedBusiness && business.id !== selectedBusiness.id) {
      opacity = 0.5;
    }

    return opacity;
  }

  function getStatusColor(business: Business) {
    let color = theme.colors.openMarkerColor;

    if (business.status === 'Closed') {
      color = theme.colors.closedMarkerColor;
    } else if (business.status === 'Active') {
      color = theme.colors.activeMarkerColor;
    }

    return color;
  }

  function getMapMarkerIcon(business: Business) {
    const svgMarker = {
      path: 'M14 0C6.26 0 0 6.26 0 14C0 24.5 14 40 14 40C14 40 28 24.5 28 14C28 6.26 21.74 0 14 0ZM14 19C11.24 19 9 16.76 9 14C9 11.24 11.24 9 14 9C16.76 9 19 11.24 19 14C19 16.76 16.76 19 14 19Z',
      fillColor: getStatusColor(business),
      fillOpacity: 1,
      strokeWeight: 1,
      strokeColor: colorScheme === 'dark' ? 'white' : 'black',
      rotation: 0,
      scale: 1,
      // these both need anchors
      anchor: new google.maps.Point(14, 40),
      size: new google.maps.Size(28, 40),
    };

    return svgMarker;
  }

  function getUserLocationIcon() {
    const svgMarker = {
      path: 'M 25, 50 a 25,25 0 1,1 50,0 a 25,25 0 1,1 -50,0',
      fillColor: '#0071BC',
      fillOpacity: 1,
      strokeWeight: 1,
      strokeColor: 'white',
      rotation: 0,
      scale: 0.25,
      anchor: new google.maps.Point(25, 25),
    };

    return svgMarker;
  }

  if (loadError) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <Text>
          There was an issue loading loading the map. Please reload the page and
          try again.
        </Text>
      </View>
    );
  }

  const filteredBusinesses = businesses.filter(
    (b) => filterOptions.length === 0 || filterOptions.includes(b.status)
  );

  const fuse = new Fuse(filteredBusinesses, {
    keys: ['name', 'address'],
    isCaseSensitive: false,
    includeScore: true,
    threshold: 0.4,
  });

  const searchResults =
    businessSearchText !== ''
      ? fuse.search(businessSearchText).map((s) => s.item)
      : filteredBusinesses;

  return isLoaded && !isLoadingUserLocation ? (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      onLoad={onLoad}
      onUnmount={onUnmount}
      // remove selected if not clicking on marker
      onClick={() => {
        if (selectedBusiness) {
          dispatch(setSelectedBusiness(null));
        }
      }}
      options={{
        disableDefaultUI: true,
        clickableIcons: false,
        styles: colorScheme === 'dark' ? darkMapStyle : lightMapStyle,
      }}
    >
      <Marker
        key={'user-location'}
        position={{ lat: usersLocation.latitude, lng: usersLocation.longitude }}
        icon={getUserLocationIcon()}
      />
      {searchResults.map((business) => {
        return (
          <Marker
            icon={getMapMarkerIcon(business)}
            position={{ lat: business.latitude, lng: business.longitude }}
            key={business.id}
            onClick={() => onMarkerClick(business)}
            opacity={getMarkerOpacity(business)}
            animation={
              business.id === selectedBusiness?.id
                ? google.maps.Animation.BOUNCE
                : undefined
            }
          />
        );
      })}
    </GoogleMap>
  ) : (
    <View
      style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.colors.backdrop,
      }}
    >
      <ActivityIndicator size="large" />
    </View>
  );
}

export default memo(WebMap);
